import { useRefs } from '@/scripts/hooks';
import { chatApis } from '@/configs/apis/chat';
import { useSessionContext } from '@/contexts/session/context';
import { pushLog } from '@/scripts/utils/devUtils';
import type { ChatChatId, ChatQuery } from '../useChats/types';
import type {
  ChatMessage,
  ChatMessagesArgParams,
  ChatMessagesMap,
  ChatMessagesArgIds,
  ChatMessagesInfoMap,
  ChatMessageQuery,
  ChatMessageId,
  ChatMessagesLoadParams,
  DBChatMessagesArgPartial,
} from './types';
import type { GetChatMessagesUtil } from './utils';
import {
  genChatMessage,
  genChatMessages,
  getChatMessagesByMap,
  mergeMessage,
} from './utils';

interface ChatMessagesState {
  map: ChatMessagesMap;
}

type ChatMessagesStateActions =
  | { type: 'init'; payload?: ChatMessageId[] }
  | { type: 'insertMessages'; payload: ChatMessagesArgParams[] }
  | { type: 'addMessages'; payload: ChatMessagesArgParams[] }
  | { type: 'updateMessages'; payload: ChatMessagesArgParams[] }
  | { type: 'removeMessages'; payload: ChatMessagesArgIds[] }
  | { type: 'clearAllMessages'; payload: ChatMessagesArgParams[] }
  | {
      type: 'getMessagesUtil';
      payload: ChatQuery;
      cb: (util: GetChatMessagesUtil) => void;
    }
  | {
      type: 'getLastMessage';
      payload: ChatQuery;
      cb: (msg: ChatMessage | undefined) => void;
    };

function genChatMessagesState(): ChatMessagesState {
  return {
    map: {},
  };
}

export function useChatMessagesState() {
  const [state, dispatch] = useReducer(
    (state: ChatMessagesState, action: ChatMessagesStateActions) => {
      let state_ = { ...state };

      function setMessages(id: ChatChatId, messages: ChatMessage[]) {
        state_.map = { ...state_.map };

        const messagesInfoMap: ChatMessagesInfoMap = {};

        messages
          .sort((a, b) => a.id - b.id)
          .forEach((message, index) => {
            const info = {
              index,
              message,
            };
            messagesInfoMap[message.id] = info;
            if (message.message_id) {
              messagesInfoMap[message.message_id] = info;
            }
            if (message.cli_msg_id) {
              messagesInfoMap[message.cli_msg_id] = info;
            }
          });

        state_.map[id] = {
          messages,
          messagesInfoMap,
        };
      }

      switch (action.type) {
        case 'init':
          if (action.payload) {
            state_.map = { ...state_.map };
            action.payload.forEach((id) => {
              delete state_.map[id];
            });
          } else {
            state_ = genChatMessagesState();
          }
          break;
        case 'insertMessages':
          action.payload.forEach((params) => {
            const { messages, getInfo } = getChatMessagesByMap(
              state_.map,
              params.chat_id
            );
            const messages_ = [...messages];
            const addMessages: ChatMessage[] = [];

            const map = new Map();
            params.messages.forEach((message) => {
              const messageInfo = getInfo(message);
              if (messageInfo) {
                const message_ = mergeMessage(map, messageInfo.message, message);
                messages_.splice(messageInfo.index, 1, message_);
              } else {
                addMessages.push(genChatMessage(message));
              }
            });

            setMessages(params.chat_id, messages_.concat(addMessages));
          });
          break;
        case 'addMessages':
          action.payload.forEach((params) => {
            const { messages } = getChatMessagesByMap(state_.map, params.chat_id);
            setMessages(params.chat_id, [
              ...messages,
              ...genChatMessages(params.messages),
            ]);
          });
          break;
        case 'updateMessages':
          action.payload.forEach((params) => {
            const { messages, getInfo } = getChatMessagesByMap(
              state_.map,
              params.chat_id
            );
            const messages_ = [...messages];

            const map = new Map();
            params.messages.forEach((message) => {
              const messageInfo = getInfo(message);
              if (messageInfo) {
                const message_ = mergeMessage(map, messageInfo.message, message);
                messages_.splice(messageInfo.index, 1, message_);
              }
            });

            setMessages(params.chat_id, messages_);
          });
          break;
        case 'removeMessages':
          action.payload.forEach((params) => {
            const { messages } = getChatMessagesByMap(state_.map, params.chat_id);
            const messages_ = messages.filter(
              (message) => !params.messages.includes(message.id)
            );
            setMessages(params.chat_id, messages_);
          });
          break;
        case 'clearAllMessages':
          action.payload.forEach((params) => {
            setMessages(params.chat_id, []);
          });
          break;
        case 'getMessagesUtil':
          action.cb(getChatMessagesByMap(state.map, action.payload.chat_id));
          break;
        case 'getLastMessage': {
          const { messages } = getChatMessagesByMap(state.map, action.payload.chat_id);
          action.cb(messages.at(-1));
          break;
        }
      }
      pushLog('messages:', { state, action });
      return state_;
    },
    genChatMessagesState()
  );

  const getMessagesUtil = useCallback(
    (id: ChatChatId) => {
      return getChatMessagesByMap(state.map, id);
    },
    [state.map]
  );

  const getMessages = useCallback(
    (id: ChatChatId) => {
      return getMessagesUtil(id).messages;
    },
    [getMessagesUtil]
  );

  const getMessage = useCallback(
    (id: ChatChatId, messageQuery: ChatMessageQuery) => {
      return getMessagesUtil(id).get(messageQuery);
    },
    [getMessagesUtil]
  );

  const getMessagesUtilAsync = useCallback((id: ChatChatId) => {
    return new Promise<GetChatMessagesUtil>((resolve) => {
      dispatch({ type: 'getMessagesUtil', payload: { chat_id: id }, cb: resolve });
    });
  }, []);

  const getMessagesAsync = useCallback(
    async (id: ChatChatId) => {
      return (await getMessagesUtilAsync(id)).messages;
    },
    [getMessagesUtilAsync]
  );

  const getMessageAsync = useCallback(
    async (id: ChatChatId, messageQuery: ChatMessageQuery) => {
      return (await getMessagesUtilAsync(id)).get(messageQuery);
    },
    [getMessagesUtilAsync]
  );

  const getLastMessage = useCallback((id: ChatChatId) => {
    return new Promise<ChatMessage | undefined>((resolve) => {
      dispatch({ type: 'getLastMessage', payload: { chat_id: id }, cb: resolve });
    });
  }, []);

  const insertMessages = useCallback((messages: ChatMessagesArgParams[]) => {
    dispatch({ type: 'insertMessages', payload: messages });
  }, []);

  const addMessages = useCallback((messages: ChatMessagesArgParams[]) => {
    dispatch({ type: 'addMessages', payload: messages });
  }, []);

  const updateMessages = useCallback((messages: ChatMessagesArgParams[]) => {
    dispatch({ type: 'updateMessages', payload: messages });
  }, []);

  const removeMessages = useCallback((messageIds: ChatMessagesArgIds[]) => {
    dispatch({ type: 'removeMessages', payload: messageIds });
  }, []);

  const initMessages = useCallback((messageIds?: ChatMessageId[]) => {
    dispatch({ type: 'init', payload: messageIds });
  }, []);

  const clearAllMessages = useCallback((messages: ChatMessagesArgParams[]) => {
    dispatch({ type: 'clearAllMessages', payload: messages });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
      getMessagesUtil,
      getMessages,
      getMessage,
      getMessagesUtilAsync,
      getMessagesAsync,
      getMessageAsync,
      getLastMessage,
      insertMessages,
      addMessages,
      updateMessages,
      removeMessages,
      initMessages,
      clearAllMessages,
    }),
    [
      updateMessages,
      getMessage,
      getMessages,
      getMessagesUtil,
      getMessagesUtilAsync,
      getMessagesAsync,
      getMessageAsync,
      getLastMessage,
      initMessages,
      removeMessages,
      state,
      insertMessages,
      addMessages,
      clearAllMessages,
    ]
  );

  return value;
}

export function useChatMessagesDB() {
  const getMessages = useCallback(async (params: ChatMessagesLoadParams) => {
    return chatApis.getMessages(params);
  }, []);

  const getUnreadMessageCount = useCallback(async (chatId: ChatChatId, id: number) => {
    return chatApis.getUnreadMessageCount(chatId, id);
  }, []);

  const deleteExpiredMessages = useCallback(
    async (chatId: ChatChatId, timestamp: number) => {
      return chatApis.deleteExpiredMessages(chatId, timestamp);
    },
    []
  );

  const getMessage = useCallback(
    async (chatId: ChatChatId, params: ChatMessageQuery) => {
      return chatApis.getMessage(chatId, params);
    },
    []
  );

  const getLastMessage = useCallback(async (chatId: ChatChatId) => {
    return chatApis.getLastMessage(chatId);
  }, []);

  const insertMessages = useCallback((paramsList: DBChatMessagesArgPartial[]) => {
    return chatApis.insertMessages(paramsList);
  }, []);

  const addMessages = useCallback((paramsList: DBChatMessagesArgPartial[]) => {
    return chatApis.addMessages(paramsList);
  }, []);

  const updateMessages = useCallback((paramsList: DBChatMessagesArgPartial[]) => {
    return chatApis.updateMessages(paramsList);
  }, []);

  const removeMessages = useCallback((paramsList: ChatMessagesArgIds[]) => {
    return chatApis.removeMessages(paramsList);
  }, []);

  const value = useMemo(
    () => ({
      getMessages,
      getUnreadMessageCount,
      deleteExpiredMessages,
      getMessage,
      getLastMessage,
      insertMessages,
      addMessages,
      updateMessages,
      removeMessages,
    }),
    [
      getMessages,
      getUnreadMessageCount,
      deleteExpiredMessages,
      getMessage,
      getLastMessage,
      insertMessages,
      addMessages,
      updateMessages,
      removeMessages,
    ]
  );

  return value;
}

export function useChatMessages() {
  const {
    session: { user },
  } = useSessionContext();
  const messagesState = useChatMessagesState();
  const messagesDB = useChatMessagesDB();
  const refs = useRefs({
    messagesState,
    messagesDB,
    initMessages() {},
  });

  const getUnreadMessageCount = useCallback(
    (chat_id: ChatChatId, last_read_msg_id: number) =>
      messagesDB.getUnreadMessageCount(chat_id, last_read_msg_id),
    [messagesDB]
  );

  const deleteExpiredMessages = useCallback(
    async (chat_id: ChatChatId, timestamp: number, messageIds: ChatMessageId[]) => {
      await messagesDB.deleteExpiredMessages(chat_id, timestamp);
      messagesState.removeMessages([{ chat_id, messages: messageIds }]);
    },
    [messagesDB, messagesState]
  );

  const deleteExpiredMessagesByIds = useCallback(
    async (chat_id: ChatChatId, messageIds: ChatMessageId[]) => {
      await messagesDB.removeMessages([{ chat_id, messages: messageIds }]);
      messagesState.removeMessages([{ chat_id, messages: messageIds }]);
    },
    [messagesDB, messagesState]
  );

  const loadMessages = useCallback(
    async (params: ChatMessagesLoadParams) => {
      const { messagesState, messagesDB } = refs.current;
      const messagesArg = await messagesDB.getMessages(params);
      messagesState.insertMessages([messagesArg]);
      return messagesArg;
    },
    [refs]
  );

  const getMessage = useCallback(
    async (chatId: ChatChatId, params: ChatMessageQuery) => {
      return (
        (await messagesState.getMessageAsync(chatId, params)) ||
        (await messagesDB.getMessage(chatId, params))
      );
    },
    [messagesDB, messagesState]
  );

  const getLastMessage = useCallback(
    async (chatId: ChatChatId) => {
      const dbLastMessage = await messagesDB.getLastMessage(chatId);
      const stateLastMessage =
        dbLastMessage && (await messagesState.getMessageAsync(chatId, dbLastMessage));

      return stateLastMessage || dbLastMessage;
    },
    [messagesDB, messagesState]
  );

  const insertMessages = useCallback(
    async (paramsList: DBChatMessagesArgPartial[]) => {
      const { messagesState, messagesDB } = refs.current;
      const messages = await messagesDB.insertMessages(paramsList);
      messagesState.insertMessages(messages);
      return messages;
    },
    [refs]
  );

  const addMessages = useCallback(
    async (paramsList: DBChatMessagesArgPartial[]) => {
      const { messagesState, messagesDB } = refs.current;
      const messages = await messagesDB.addMessages(paramsList);
      messagesState.addMessages(messages);
      return messages;
    },
    [refs]
  );

  const updateMessages = useCallback(
    async (paramsList: DBChatMessagesArgPartial[]) => {
      const { messagesState, messagesDB } = refs.current;
      await messagesDB.updateMessages(paramsList);
      messagesState.updateMessages(paramsList);
    },
    [refs]
  );

  const removeMessages = useCallback(
    async (paramsList: ChatMessagesArgIds[]) => {
      const { messagesState, messagesDB } = refs.current;
      await messagesDB.removeMessages(paramsList);
      messagesState.removeMessages(paramsList);
    },
    [refs]
  );

  const initMessages = useCallback(async () => {
    const { messagesState } = refs.current;
    await messagesState.initMessages();
  }, [refs]);

  refs.current.initMessages = initMessages;

  const value = useMemo(
    () => ({
      messagesState,
      messagesDB,
      loadMessages,
      insertMessages,
      addMessages,
      updateMessages,
      removeMessages,
      initMessages,
      getMessage,
      getLastMessage,
      getUnreadMessageCount,
      deleteExpiredMessages,
      deleteExpiredMessagesByIds,
    }),
    [
      initMessages,
      loadMessages,
      messagesDB,
      messagesState,
      removeMessages,
      insertMessages,
      addMessages,
      updateMessages,
      getMessage,
      getLastMessage,
      getUnreadMessageCount,
      deleteExpiredMessages,
      deleteExpiredMessagesByIds,
    ]
  );

  useLayoutEffect(() => {
    if (user) {
      const { initMessages } = refs.current;
      initMessages();
    }
  }, [refs, user]);

  return value;
}
